'use client'

import * as React from 'react'

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { twMerge } from 'tailwind-merge'

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitives.Root
    className={twMerge(
      'peer inline-flex h-[26px] w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 dark:focus-visible:ring-primary-600 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-accent-400 dark:data-[state=checked]:bg-accent-400 data-[state=unchecked]:bg-primary-200 dark:data-[state=unchecked]:bg-primary-800',
      className,
    )}
    {...props}
    ref={ref}
  >
    <SwitchPrimitives.Thumb
      className={twMerge(
        'pointer-events-none block h-5 w-5 rounded-full bg-white dark:bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-[1px]',
      )}
    />
  </SwitchPrimitives.Root>
))
Switch.displayName = SwitchPrimitives.Root.displayName

/**
 * Wrapper that shields a Switch from Radix Tooltip's `data-state` override.
 *
 * When a PermissionGate (or any Radix Tooltip) wraps a Switch via `asChild`,
 * the Tooltip's `data-state="closed"` overwrites the Switch's
 * `data-state="checked"`, killing its checked background color.
 *
 * This span absorbs the Tooltip's data-state so the Switch keeps its own.
 * It also handles disabled styling when the wrapper receives `aria-disabled`.
 *
 * When the gate marks the wrapper `aria-disabled`, we also propagate a real
 * `disabled` onto the child Switch. The CSS `pointer-events-none` only blocks
 * mouse input; without disabling the Radix root, a keyboard user could still
 * Tab to the switch and toggle it with Space/Enter. (The server independently
 * enforces the permission, so this is defense-in-depth + a11y correctness.)
 */
const SwitchGateWrapper = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ children, ...props }, ref) => {
  const ariaDisabled = props['aria-disabled']
  const isGateDisabled = ariaDisabled === true || ariaDisabled === 'true'
  const content =
    isGateDisabled && React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement<{ disabled?: boolean }>,
          { disabled: true },
        )
      : children
  return (
    <span
      ref={ref}
      className="inline-flex [&[aria-disabled=true]]:cursor-not-allowed [&[aria-disabled=true]]:opacity-50 [&[aria-disabled=true]>*]:pointer-events-none"
      {...props}
    >
      {content}
    </span>
  )
})
SwitchGateWrapper.displayName = 'SwitchGateWrapper'

export { Switch, SwitchGateWrapper }
